import React, { createContext, useState, useContext } from 'react';
import { firestore, storage, timestamp } from '../firebase/config';
import { collection, addDoc, updateDoc, doc, getDoc, getDocs, query, where, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

// Création du contexte des véhicules
const VehicleContext = createContext();

// Hook personnalisé pour utiliser le contexte des véhicules
export const useVehicle = () => {
  return useContext(VehicleContext);
};

// Fournisseur du contexte des véhicules
export const VehicleProvider = ({ children }) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fonction pour récupérer tous les véhicules
  const getVehicles = async () => {
    try {
      setLoading(true);
      const vehiclesCollection = collection(firestore, 'vehicles');
      const vehiclesSnapshot = await getDocs(vehiclesCollection);
      const vehiclesList = vehiclesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setVehicles(vehiclesList);
      setLoading(false);
      return vehiclesList;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Fonction pour récupérer un véhicule par ID
  const getVehicleById = async (id) => {
    try {
      const vehicleDoc = await getDoc(doc(firestore, 'vehicles', id));
      if (vehicleDoc.exists()) {
        return { id: vehicleDoc.id, ...vehicleDoc.data() };
      } else {
        return null;
      }
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fonction pour créer un nouveau véhicule
  const createVehicle = async (vehicleData, imageFile) => {
    try {
      let imageUrl = null;
      
      // Si une image est fournie, la télécharger sur Firebase Storage
      if (imageFile) {
        const storageRef = ref(storage, `vehicles/${Date.now()}_${imageFile.name}`);
        await uploadBytes(storageRef, imageFile);
        imageUrl = await getDownloadURL(storageRef);
      }
      
      const newVehicle = {
        ...vehicleData,
        imageUrl,
        status: 'active',
        createdAt: timestamp(),
        updatedAt: timestamp()
      };
      
      const docRef = await addDoc(collection(firestore, 'vehicles'), newVehicle);
      return { id: docRef.id, ...newVehicle };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fonction pour mettre à jour un véhicule
  const updateVehicle = async (id, vehicleData, imageFile) => {
    try {
      const vehicleRef = doc(firestore, 'vehicles', id);
      const updatedData = { ...vehicleData, updatedAt: timestamp() };
      
      // Si une nouvelle image est fournie, la télécharger et mettre à jour l'URL
      if (imageFile) {
        // Récupérer l'ancienne image pour la supprimer si elle existe
        const oldVehicle = await getVehicleById(id);
        if (oldVehicle && oldVehicle.imageUrl) {
          try {
            const oldImageRef = ref(storage, oldVehicle.imageUrl);
            await deleteObject(oldImageRef);
          } catch (error) {
            console.error("Erreur lors de la suppression de l'ancienne image:", error);
          }
        }
        
        // Télécharger la nouvelle image
        const storageRef = ref(storage, `vehicles/${Date.now()}_${imageFile.name}`);
        await uploadBytes(storageRef, imageFile);
        updatedData.imageUrl = await getDownloadURL(storageRef);
      }
      
      await updateDoc(vehicleRef, updatedData);
      return { id, ...updatedData };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fonction pour désactiver un véhicule
  const deactivateVehicle = async (id, reason) => {
    try {
      const vehicleRef = doc(firestore, 'vehicles', id);
      await updateDoc(vehicleRef, {
        status: 'inactive',
        deactivationReason: reason,
        deactivatedAt: timestamp(),
        updatedAt: timestamp()
      });
      return { id, status: 'inactive', deactivationReason: reason };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fonction pour ajouter un événement de maintenance
  const addMaintenanceEvent = async (vehicleId, eventData) => {
    try {
      const vehicleRef = doc(firestore, 'vehicles', vehicleId);
      const vehicle = await getVehicleById(vehicleId);
      
      if (!vehicle) {
        throw new Error("Véhicule non trouvé");
      }
      
      const maintenanceEvents = vehicle.maintenanceEvents || [];
      const newEvent = {
        ...eventData,
        id: Date.now().toString(),
        date: timestamp(),
        updatedAt: timestamp()
      };
      
      maintenanceEvents.push(newEvent);
      
      await updateDoc(vehicleRef, {
        maintenanceEvents,
        updatedAt: timestamp()
      });
      
      return newEvent;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Valeur du contexte
  const value = {
    vehicles,
    loading,
    error,
    getVehicles,
    getVehicleById,
    createVehicle,
    updateVehicle,
    deactivateVehicle,
    addMaintenanceEvent
  };

  return (
    <VehicleContext.Provider value={value}>
      {children}
    </VehicleContext.Provider>
  );
};

export { VehicleContext };
